import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import useToggle from 'next/hooks/use-toggle';
import Logo from './Logo';

/**
 * NOTE: Since we are using a rounded border for the arcs, we are currently
 *       unable to have the edges of the arcs also rounded. If we want to
 *       implement the rounded corners in the future, we can look into
 *       restructuring the component and styles a bit to support that feature.
 */

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50%{
    transform: scale(1.2);
  }
  100%{
    transform: scale(1);
  }
`;

const merge = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.2);
  }
`;

const spinner = keyframes`
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const move = $position => keyframes`
  0% {
    left: 18px;
    ${$position}: 18px;
  }
  100% {
    left: 18px;
    ${$position}: 42px;
  }
`;

const grow = $position => keyframes`
  0% {
    background: transparent;
    border: 4px solid transparent;

    ${
      $position === 'top'
        ? `border-bottom-color: var(--primary);`
        : `border-top-color: var(--primary);`
    }
  }
  100% {
    background: transparent;
    border: 4px solid transparent;
    height: 50px;
    left: -5px;
    width: 50px;

    ${$position}: -2px;

    ${
      $position === 'top'
        ? `border-bottom-color: var(--primary);`
        : `border-top-color: var(--primary);
          `
    }
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

const Rotate = styled.div`
  animation: ${pulse} 0.6s ease-in-out 1, ${spinner} 1.5s linear 0.5s infinite;
  background: var(--primary-grad);
  border-radius: 50%;
  display: block;
  grid-column-start: 1;
  grid-row-start: 1;
  height: 40px;
  position: relative;
  transform: translateZ(0);
  width: 40px;
  will-change: transform;
`;

const Arc = styled.div`
  background: var(--primary);
  border-radius: 50%;
  height: 6px;
  left: 18px;
  position: absolute;
  width: 6px;
  transform: translateZ(0);
  will-change: transform;

  ${({ $position }) => css`
    animation: ${move($position)} 0.5s linear 1 0.5s forwards,
      ${grow($position)} 0.5s linear 1 1s forwards,
      ${merge} 0.5s linear 1 3s forwards;

    ${$position}: 18px;
  `}
`;

const IconWrapper = styled.div`
  align-items: center;
  animation: ${pulse} 0.6s ease-in-out 1;
  background: var(--primary-grad);
  border-radius: 100%;
  display: flex;
  grid-column-start: 1;
  grid-row-start: 1;
  height: 40px;
  justify-content: center;
  position: absolute;
  transform: translateZ(0);
  width: 40px;
  will-change: transform;
`;

const Icon = styled(Logo)`
  color: var(--white);
  font-size: var(--large);
`;

const Spinner = props => {
  const $rotate = useRef();
  const $top = useRef();
  const $bottom = useRef();

  const [key, reset] = useToggle();

  useEffect(() => {
    // Restart the animation by forcing React to remount the spinner component
    // via setting a new key value
    const timer = setInterval(reset, 3600);

    return () => {
      clearInterval(timer);
    };
  }, [reset]);

  return (
    <Wrapper aria-busy aria-live="polite" key={key} {...props}>
      <Rotate ref={$rotate}>
        <Arc $position="top" ref={$top} />
        <Arc $position="bottom" ref={$bottom} />
      </Rotate>

      <IconWrapper>
        <Icon />
      </IconWrapper>
    </Wrapper>
  );
};

Spinner.propTypes = {
  className: PropTypes.string,
};

export default styled(Spinner)``;
